import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels } from "@/i18n";

const CRASH_AREAS = ["Fram", "Bak", "Vänster", "Höger", "Annat"] as const;

interface CrashSectionProps {
  crash: { problem?: string; area?: string; other?: string } | null;
  onCrashChange: (crash: {
    problem?: string;
    area?: string;
    other?: string;
  }) => void;
  onNext: () => void;
}

export const CrashSection = ({
  crash,
  onCrashChange,
  onNext,
}: CrashSectionProps) => {
  const [otherText, setOtherText] = useState(crash?.other || "");
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const form = ct.form ?? {};
  const v = ct.pdf?.values ?? ({} as Record<string, string>);
  const opt =
    (v as { optionValues?: Record<string, string> }).optionValues ?? {};

  const handleResultChange = (problem: string) => {
    if (problem === "no" || problem === "unknown") {
      onCrashChange({ problem });
    } else {
      onCrashChange({ problem, area: undefined, other: undefined });
    }
  };

  const handleAreaChange = (area: string) => {
    if (area === "Annat") {
      onCrashChange({ ...crash, area, other: otherText });
    } else {
      onCrashChange({ ...crash, area, other: undefined });
    }
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    if (crash?.area === "Annat") {
      onCrashChange({ ...crash, other: text });
    }
  };

  const showNextButton =
    crash?.problem === "yes" && crash?.area === "Annat" && otherText.trim();

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium text-sm mb-3">{form.questionCrash}</h4>
        <div className="grid grid-cols-3 gap-1.5 mb-4">
          <Button
            onClick={() => handleResultChange("yes")}
            variant="formOption"
            className={`${
              crash?.problem === "yes"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.yes}
          </Button>
          <Button
            onClick={() => handleResultChange("no")}
            variant="formOption"
            className={`${
              crash?.problem === "no"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.no}
          </Button>
          <Button
            onClick={() => handleResultChange("unknown")}
            variant="formOption"
            className={`${
              crash?.problem === "unknown"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.unknown}
          </Button>
        </div>

        {crash?.problem === "yes" && (
          <div className="space-y-3">
            <h5 className="font-medium text-sm">{form.questionCrashArea}</h5>
            <div className="grid grid-cols-2 gap-1.5">
              {CRASH_AREAS.map((area) => (
                <Button
                  key={area}
                  onClick={() => handleAreaChange(area)}
                  variant="formOption"
                  className={`${
                    crash?.area === area
                      ? "border-accent border-2"
                      : "border-gray-400"
                  }`}
                >
                  {opt[area] ?? area}
                </Button>
              ))}
            </div>

            {crash?.area === "Annat" && (
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder={form.placeholderDescribeCrashArea}
                  value={otherText}
                  onChange={(e) => handleOtherTextChange(e.target.value)}
                  className="w-full"
                />
              </div>
            )}
          </div>
        )}

        {showNextButton && (
          <Button className="w-full mt-4" onClick={onNext}>
            {form.next}
          </Button>
        )}
      </div>
    </div>
  );
};
